import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@acs/ui';
import { useI18n } from '../../i18n/index.js';
import type { CreateManualBookingRequest, RescheduleBookingRequest } from '@acs/shared';
import {
  cancelAppointment,
  createManualBooking,
  rescheduleBooking,
  subscribeAppointmentsInRange,
  subscribeUpcoming,
} from '../../shared/api/appointments.js';
import { subscribeCalls } from '../../shared/api/calls.js';
import type { AppointmentDoc, CallDoc } from '../../shared/api/converters.js';
import { keys } from '../../shared/api/keys.js';
import { useLiveQuery } from '../../shared/api/useLiveQuery.js';
import { summariseCalls, type DashboardStats } from './dashboardStats.js';

export function useUpcomingAppointments(businessId: string) {
  return useLiveQuery<AppointmentDoc[]>(keys.upcomingAppointments(businessId), (cb, onError) =>
    subscribeUpcoming(businessId, cb, onError),
  );
}

const DAY_MS = 86_400_000;

/** Confirmed appointments around a Monday-start week. The UTC query range is
 * padded a day each side so every business timezone's local week is covered;
 * the calendar drops instants that fall outside its 7 local dates. */
export function useWeekAppointments(businessId: string, weekStartIso: string) {
  const weekStartUtc = Date.parse(`${weekStartIso}T00:00:00Z`);
  const from = new Date(weekStartUtc - DAY_MS);
  const to = new Date(weekStartUtc + 8 * DAY_MS);
  return useLiveQuery<AppointmentDoc[]>(keys.weekAppointments(businessId, weekStartIso), (cb, onError) =>
    subscribeAppointmentsInRange(businessId, from, to, cb, onError),
  );
}

/** KPI strip source: the last seven days of calls, already aggregated. The row
 *  cap is raised well past the list default so a busy week isn't truncated. */
export function useDashboardStats(
  businessId: string,
  timezone: string,
): { stats: DashboardStats | null; isLoading: boolean; isError: boolean } {
  // Anchored to the current hour so the key (and the query) stays stable while
  // the tab is open, instead of re-subscribing on every render.
  const from = useMemo(() => {
    const at = new Date();
    at.setMinutes(0, 0, 0);
    return new Date(at.getTime() - 6 * DAY_MS);
  }, []);
  const range = useMemo(() => ({ from }), [from]);

  const { data, isLoading, isError } = useLiveQuery<CallDoc[]>(keys.calls(businessId, range), (cb) =>
    subscribeCalls(businessId, range, cb, 500),
  );

  const stats = useMemo(() => (data ? summariseCalls(data, timezone) : null), [data, timezone]);
  return { stats, isLoading, isError };
}

export function useCancelAppointment(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (appointmentId: string) => cancelAppointment(businessId, appointmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.upcomingAppointments(businessId) }),
  });
}

/** Owner books by hand (doc 43). The calendar and list are live listeners, so
 * nothing needs invalidating — the new booking arrives on its own. */
export function useCreateManualBooking() {
  const { t } = useI18n();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (req: CreateManualBookingRequest) => createManualBooking(req),
    onSuccess: () => toast({ title: t('bookingForm.created'), tone: 'success' }),
  });
}

/**
 * Owner moves a booking to a new time (doc 43 D2).
 *
 * The confirmation lives HERE, not in a `mutate(…, { onSuccess })` at the call
 * site: the move cancels the original, the calendar's confirmed-only listener
 * drops it, and the dialog showing it unmounts — usually before the callable's
 * response arrives. Per-call callbacks of an unmounted observer never run;
 * these do.
 */
export function useRescheduleBooking() {
  const { t } = useI18n();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (req: RescheduleBookingRequest) => rescheduleBooking(req),
    onSuccess: () => toast({ title: t('bookingForm.moved'), tone: 'success' }),
  });
}
